/**
 * The create page's *courtesy* gate: which of the invoice actions this
 * session's loaded effective-access answer says it holds.
 *
 * This decides nothing. The gateway decides, per request, from the session (or
 * the API key) it sees; this only stops the page from inviting a person to
 * press a button the loaded contract already says is not theirs. If the scope
 * is absent the controls are **disabled with the reason on screen** — never
 * hidden, and never replaced by a denied page, because a page that claims
 * "denied" on the strength of a client-side list would be reporting a decision
 * the service has not made.
 *
 * It reads `experience.grantedScopes` directly rather than going through
 * `usePermission`. `Permission` (`src/lib/permissions.ts`) carries no invoice
 * scopes, and that shared union is not this task's to extend; the effective
 * access contract's scope list is the actual thing the gateway gates these
 * routes on, and reading it here is the same pattern
 * `useSignerPairingAllowed` uses for the signer-pairing scope.
 *
 * Fails closed: until a real effective-access answer has loaded
 * (`source === "api"`), every action is treated as not-yet-known and stays
 * disabled — an unknown scope is not a granted one.
 */

import { useAuthStore } from "@/stores/auth-store";

/** Scope names, exactly as the page spec's `expected_allows` records them. */
export const INVOICE_SCOPES = {
  create: "invoice:create",
  issue: "invoice:issue",
  managePayers: "invoice:payer.manage",
} as const;

export interface InvoiceScopes {
  /** A real effective-access answer has loaded. */
  loaded: boolean;
  canCreate: boolean;
  canIssue: boolean;
  canManagePayers: boolean;
}

export function useInvoiceScopes(): InvoiceScopes {
  const experience = useAuthStore((state) => state.experience);
  const loaded = experience.source === "api";
  const granted = experience.grantedScopes;
  const holds = (scope: string) => loaded && granted.includes(scope);
  return {
    loaded,
    canCreate: holds(INVOICE_SCOPES.create),
    canIssue: holds(INVOICE_SCOPES.issue),
    canManagePayers: holds(INVOICE_SCOPES.managePayers),
  };
}
